import { Component } from '@angular/core';
import { LoadingController, ModalController, ToastController } from '@ionic/angular';
import { ActivatedRoute, Router } from '@angular/router';
import { EmployeeGoals } from '../../_interfaces/employee-goals.interface';
import { GoalsService } from '../../goals.service';
import { BaseClass } from 'src/app/shared/classes/base';
import { GoalFeedback } from '../../_interfaces/goal-feedback.interface';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { Goal, GoalStatusEnum } from '../../_interfaces/goal.interface';
import { ProcessConfig } from 'src/app/shared/interfaces/process-config.interface';
import { UserPreview } from 'src/app/shared/interfaces/user-preview.interface';
import { UtilService } from 'src/app/shared/services/util.service';
import { ViewGoalsPage } from 'src/app/dashboard/goals-progress/view-goals/view-goals.page';
import { NewGoalsInput } from 'src/app/shared/modals/new-goals-input/new-goals-input';
import { ProcessStatusEnum } from 'src/app/shared/interfaces/process.interface';
import { AttributeTypeEnum } from 'src/app/shared/modals/filters/models/filter-models';

@Component({
    selector: 'app-goals-input-review',
    templateUrl: './goals-input-review.page.html',
    styleUrls: ['./goals-input-review.page.scss']
})
export class GoalsInputReviewPage extends BaseClass {

    public subordinateGoals: EmployeeGoals;
    public userPreview: UserPreview;
    public processConfig: ProcessConfig;
    public displayedGoals: Array<Goal> = [];
    public selectedLanguage: string;
    public stepStatus: ProcessStatusEnum;

    public readonly ProcessStatusEnum = ProcessStatusEnum;

    private _employeeId: string;
    private _participantId: string;
    private readonly _cycleId: number;

    constructor(
            protected _toastController: ToastController,
            protected _translationService: TranslationService,
            private _modalController: ModalController,
            private _loadingController: LoadingController,
            private _router: Router,
            private _goalsService: GoalsService,
            private _activatedRoute: ActivatedRoute,
            private _utilService: UtilService
    ) {
        super(_toastController, _translationService);
        this._employeeId = this._activatedRoute.snapshot.paramMap.get('goalId');
        this._cycleId = parseInt(
                this._activatedRoute.snapshot.paramMap.get('cycleId'), 10
        );
    }

    ionViewWillEnter() {
        this.selectedLanguage = this._translationService.getSelectedLanguage();
        this._loadGoals(this._cycleId);
    }

    public canApproveGoals(goals?: Array<Goal>): boolean {
        return true; // goals && goals.some(goal => goal.status === GoalStatusEnum.WaitingForApproval);
    }

    public getColumnSize(questionsCount: number): number {
        let tableSize = 10;
        if (this.processConfig) {
            tableSize = this.processConfig.title ? tableSize - 2:tableSize;
            tableSize = this.processConfig.weight ? tableSize - 2:tableSize;
            tableSize = this.processConfig.deadline ? tableSize - 2:tableSize;
            tableSize = this.processConfig.description ? tableSize - 2:tableSize;
        }

        let columnSize = questionsCount ? parseFloat((tableSize / questionsCount).toFixed(2).toString()):null;

        if (columnSize)
            return columnSize;
        return questionsCount > 1 ? 1:2;
    }

    public async viewGoalProgress(event: { goal: Goal }): Promise<void> {
        const goalModal = await this._createGoalModal(this._cycleId, event.goal.id);
        goalModal.present();
    }

    private async _createGoalModal(cycleId: number, goalId: number) {
        return await this._modalController.create({
            component: ViewGoalsPage,
            cssClass: 'view-goals-modal-style',
            componentProps: {
                'goalId': goalId,
                'cycleId': cycleId,
                'allowComments': false
            }
        });
    }

    public setGoalsFeedbacks(goals: Array<Goal>): void {
        const feedbacks = this._getGoalsFeedbacks(goals);
        if (feedbacks) {
            this._saveFeedbacks(feedbacks);
        }
    }

    private _getGoalsFeedbacks(goals: Array<Goal>): Array<GoalFeedback> {
        if (goals.some(g => g.approved === null || g.approved === undefined)) {
            this.notify(
                    this._translationService
                            .getTranslation('goalsValidation.missingApprovals'),
                    'danger').finally();
            return null;
        }

        if (goals.some(g => g.approved === false && !g.managerComment)) {
            this.notify(
                    this._translationService
                            .getTranslation('goalsValidation.missingRejectedFeedback'),
                    'danger').finally();
            return null;
        }

        return goals.map(g => {
            return {
                'goalId': g.id,
                'approved': g.approved,
                'comment': g.managerComment
            };
        });
    }

    private async _saveFeedbacks(feedbacks: Array<GoalFeedback>): Promise<void> {
        if (!this._validateGoals(feedbacks)) {
            return;
        }

        const loading = await this._loadingController.create();
        await loading.present();
        this._goalsService.setGoalFeedback(
            this._participantId, feedbacks
        ).subscribe(() => {
            loading.dismiss();
            this.notify(this._translationService.getTranslation('generic.infoSavedSuccessfully'), 'success');
            this.goBack();

        }, (error) => {
            loading.dismiss();
            if (error?.error?.errors?.includes('goalsInput.maxGoalsQuantityReached')) {
                var message = this._translationService.getTranslation('goalsInput.maxGoalsQuantityReached');
                this.notify(message.replace('{{maxGoals}}', this.processConfig.maximumGoalQuantity.toString()), 'danger');
            }
            else
                this.notify(this.getErrorNotification(error), 'danger');
        });
    }

    private _validateGoals(goals: Array<GoalFeedback>) {
        if (this.processConfig && goals.length > this.processConfig.maximumGoalQuantity) {
            var message = this._translationService.getTranslation('goalsInput.maxGoalsQuantityReached');
            this.notify(message.replace('{{maxGoals}}', this.processConfig.maximumGoalQuantity.toString()), 'danger');
            return false;
        }
        return true;
    }

    public async openNewGoalModal() {
        if (this.subordinateGoals?.goals.length >= this.processConfig.maximumGoalQuantity) {
            var message = this._translationService.getTranslation('goalsInput.maxGoalsQuantityReached');
            this.notify(message.replace('{{maxGoals}}', this.processConfig.maximumGoalQuantity.toString()), 'danger');
            return;
        }

        const modal = await this._modalController.create({
            component: NewGoalsInput,
            cssClass: 'new-goals-input',
            componentProps: {
                'cycleParticipantId': this._participantId,
                'processConfig': this.processConfig,
                'processType': this.ProcessType.GoalInputs,
            }
        });

        modal.onDidDismiss().then((dismissEvent) => {
            if (dismissEvent.data) {
                this._loadGoals(this._cycleId);
            }
        });

        return await modal.present();
    }

    public async openManageGoalsInputModal(goal: Goal) {
        const modal = await this._modalController.create({
            component: NewGoalsInput,
            cssClass: 'new-goals-input',
            componentProps: {
                'cycleParticipantId': this._participantId,
                'processConfig': this.processConfig,
                'goal': goal,
                'isGoalReview': true
            }
        });

        modal.onDidDismiss().then((dismissEvent) => {
            if (dismissEvent.data) {
                this._loadGoals(this._cycleId);
            }
        });

        return await modal.present();
    }

    public goBack(): void {
        this._router.navigate(['goals-validation/' + this._cycleId]);
    }

    private async _loadGoals(cycleId: number): Promise<void> {
        const loading = await this._loadingController.create();
        await loading.present();

        this._goalsService.getSubordinateGoals(
                cycleId, this._employeeId
        ).subscribe((response) => {
            response.data.goals = this._fillApprovedProperty(response.data.goals);

            this._participantId = response.data.cycleParticipantId;
            this.subordinateGoals = response.data;
            let user = {
                name: response.data?.employee,
                imageUrl: response.data?.employeeImageUrl,
                attributes: []
            };
            this.userPreview = this._mapSubordinateGoalsToUserPreview(user);
            this.displayedGoals = response.data.goals;
            this.stepStatus = response.data.status;
            this.processConfig = response.data.processConfig;

            this.processConfig.questions = this._utilService.orderByProperty(
                    this.processConfig.questions, 'order'
            );
            loading.dismiss();
        }, (error) => {
            loading.dismiss();
            this.notify(this.getErrorNotification(error), 'danger');
        });
    }

    private _fillApprovedProperty(goals: Array<Goal>): Array<Goal> {
        goals.forEach(goal => {
            if (goal.status === GoalStatusEnum.WaitingForApproval) {
                goal.approved = null;
            } else if (goal.status === GoalStatusEnum.Approved ||
                    goal.status === GoalStatusEnum.SentForReview ||
                    goal.status === GoalStatusEnum.ReviewAccepted) {
                goal.approved = true;
            } else if (goal.status === GoalStatusEnum.Rejected ||
                    goal.status === GoalStatusEnum.ReviewRejected) {
                goal.approved = false;
            } else {
                goal.approved = null;
            }
        });

        return goals;
    }

    public getTotalWeight(): number {
        return this.displayedGoals ? this.displayedGoals.reduce((sum, goal) => sum + (goal.weight ? goal.weight:0), 0):0;
    }

    private _mapSubordinateGoalsToUserPreview(user: any): UserPreview {
        console.log(user)
        if (!user) return null;
        return {
            name: user.name,
            username: user.username,
            imageUrl: user.imageUrl,
            group: user.attributes.find(a => a.type === AttributeTypeEnum.BusinessGroup)?.value,
            rank: user.attributes.find(a => a.type === AttributeTypeEnum.Rank)?.value,
        };
    }
}
